// The BcfActivatedRoute class is a service that provides information about the active route.
// It provides the fragment, queryParams and params of the active route.
// It is used by the components to get the information about the active route.
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { distinctUntilChanged, map, of, startWith, type Observable } from 'rxjs';
import { provideBcfRouterBaseHref, type BcfRouterBaseHref } from './bcf-router-base-href';
import type { BcfRouterServerSession } from './bcf-router-server-session';
import { provideBcfRouterServerSession } from './bcf-router-server-session';
import { provideHistoryState, type HistoryState } from './history-state';
import { removeBaseHrefFromPathname } from './pure-utils/remove-base-href-from-pathname';

function mapToUrlOriginWithPathname(location: Location): string {
  return `${location.origin}${location.pathname}${location.search}`;
}

export interface BcfActivatedUrlContract {
  get url$(): Observable<string>;

  get fullUrl$(): Observable<string>;

  get originUrl$(): Observable<string>;
}

export class BcfActivatedUrl implements BcfActivatedUrlContract {
  constructor(
    private _historyState: HistoryState,
    private _serverCurrentSession: BcfRouterServerSession,
    private _bcfRouterBaseHref: BcfRouterBaseHref
  ) {}

  public get url$(): Observable<string> {
    if (__RENDER_MODE__ === 'server') {
      return of(this._serverCurrentSession.activeUrl);
    }
    return this._historyState.getState().pipe(
      startWith(undefined),
      map(() => removeBaseHrefFromPathname(window.location.pathname, this._bcfRouterBaseHref.baseHref)),
      distinctUntilChanged()
    );
  }

  public get fullUrl$(): Observable<string> {
    if (__RENDER_MODE__ === 'server') {
      return of('');
    }
    return this._historyState.getState().pipe(
      startWith(undefined),
      map(() => mapToUrlOriginWithPathname(window.location)),
      distinctUntilChanged()
    );
  }

  public get originUrl$(): Observable<string> {
    if (__RENDER_MODE__ === 'server') {
      return of('');
    }
    return this._historyState.getState().pipe(
      startWith(undefined),
      map(() => window.location.origin),
      distinctUntilChanged()
    );
  }
}

export function provideBcfActivatedUrl(): BcfActivatedUrl {
  return provideSingleton(
    BcfActivatedUrl,
    () => new BcfActivatedUrl(provideHistoryState(), provideBcfRouterServerSession(), provideBcfRouterBaseHref())
  );
}
